import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk'
import { WSS_BASE_URL, OHAIO_API_URL } from '@/constants/value.js'

export default function useWebSocketService (
  sourceLanguage,
  targetLanguage,
  roomId,
  onMessageCallback,
  diarizationEnabled = false
) {
  let deepgramClient = null
  let deepgramConnection = null
  let speakerSocket = null
  
  // Queue system for managing translations with speaker info
  let translationQueue = new Map() // Map<requestId, {speaker, originalMessage, timestamp}>
  let requestIdCounter = 0

  const fetchDeepgramApiKey = async () => {
    try {
      const response = await fetch(`${OHAIO_API_URL}/user/deepgram-token`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      const result = await response.json()
      if (result.message_code === 0 && result.data?.key) {
        return result.data.key
      }
      throw new Error('Failed to retrieve Deepgram API key')
    } catch (error) {
      console.error('Error fetching Deepgram API key:', error)
      throw error
    }
  }

  const connectToDeepgram = async sendAudioCallback => {
    try {
      const DEEPGRAM_API_KEY = await fetchDeepgramApiKey()
      deepgramClient = createClient(DEEPGRAM_API_KEY)

      const options = {
        language: sourceLanguage,
        model: 'nova-2',
        punctuate: true,
        words: true,
        diarize: true // Always enable diarization at Deepgram level
      }

      deepgramConnection = deepgramClient.listen.live(options)

      deepgramConnection.on(LiveTranscriptionEvents.Open, () => {
        console.log('Deepgram connection established')
        sendAudioCallback(deepgramConnection)
      })

      deepgramConnection.keepAlive()

      deepgramConnection.on(LiveTranscriptionEvents.Transcript, data => {
        const transcript = data.channel?.alternatives[0]?.transcript || ''

        if (data.is_final && transcript.trim() !== '') {
          console.log(transcript)
          
          let speakerInfo = null
          if (data.channel?.alternatives[0]?.words?.length > 0) {
            const firstWord = data.channel.alternatives[0].words[0]
            if (firstWord && typeof firstWord.speaker !== 'undefined') {
              speakerInfo = {
                speaker: firstWord.speaker,
                confidence: firstWord.speaker_confidence || 0
              }
            }
          }
          
          if (speakerSocket && speakerSocket.readyState === WebSocket.OPEN) {
            const requestId = `req_${++requestIdCounter}_${Date.now()}`
            translationQueue.set(requestId, {
              speaker: speakerInfo,
              originalMessage: transcript,
              timestamp: Date.now()
            })
            
            const translationRequest = {
              message: transcript,
              original_language: sourceLanguage.toUpperCase(),
              translate_user_infor: {
                user_id: roomId,
                language: targetLanguage.toUpperCase()
              }
            }
            speakerSocket.send(JSON.stringify(translationRequest))
          }
        }
      })

      deepgramConnection.on(LiveTranscriptionEvents.Error, error => {
        console.error('Deepgram error:', error)
      })

      deepgramConnection.on(LiveTranscriptionEvents.Close, () => {
        console.log('Deepgram connection closed')
      })

      return deepgramConnection
    } catch (error) {
      console.error('Error connecting to Deepgram:', error)
      throw error
    }
  }

  const connectToSpeakerSocket = async () => {
    return new Promise((resolve, reject) => {
      try {
        const wsUrl = `${WSS_BASE_URL}/${roomId}/speaker/${targetLanguage.toUpperCase()}`
        speakerSocket = new WebSocket(wsUrl)

        speakerSocket.onopen = () => {
          console.log('Speaker WebSocket connected successfully')
          resolve(speakerSocket) // Return the socket
        }

        speakerSocket.onmessage = event => {
          try {
            // Since backend only returns string, decode it directly
            const message = decodeUnicode(event.data)
            
            if (translationQueue.size > 0) {
              // Get the oldest request from queue (FIFO approach)
              const oldestEntry = translationQueue.entries().next().value
              if (oldestEntry) {
                const [oldestRequestId, queuedData] = oldestEntry
                const formattedMessage = formatMessageWithSpeaker(message, queuedData.speaker, diarizationEnabled)
                translationQueue.delete(oldestRequestId)
                
                if (onMessageCallback) onMessageCallback(formattedMessage)
              } else {
                // Fallback if queue is empty
                if (onMessageCallback) onMessageCallback(message)
              }
            } else {
              // Fallback when no queue entries
              if (onMessageCallback) onMessageCallback(message)
            }
          } catch (error) {
            console.error('Error processing speaker socket message:', error)
            // Fallback to original behavior
            const message = decodeUnicode(event.data)
            if (onMessageCallback) onMessageCallback(message)
          }
        }

        speakerSocket.onerror = error => {
          console.error('Speaker WebSocket error:', error)
          reject(new Error('Failed to connect to translation service.'))
        }

        speakerSocket.onclose = () => {
          console.log('Speaker WebSocket closed')
        }

        setTimeout(() => {
          if (!speakerSocket || speakerSocket.readyState !== WebSocket.OPEN) {
            reject(new Error('Speaker socket connection timeout'))
          }
        }, 10000)
      } catch (error) {
        console.error('Error connecting to speaker socket:', error)
        reject(error)
      }
    })
  }

  const decodeUnicode = str => {
    let decoded = str.replace(/\\u([\dA-Fa-f]{4})/g, (match, group) =>
      String.fromCharCode(parseInt(group, 16))
    )
    if (decoded.startsWith('"') && decoded.endsWith('"')) {
      return decoded.slice(1, -1)
    }
    return decoded
  }

  const formatMessageWithSpeaker = (message, speakerInfo, diarizationEnabled) => {
    if (!diarizationEnabled || !speakerInfo) {
      return message
    }
    
    const speakerLabel = `Speaker ${speakerInfo.speaker || 0}`
    const confidence = speakerInfo.confidence
    
    // Format: "[Speaker 1]: message" with confidence info in console
    if (confidence < 0.5) {
      console.warn(`Low speaker confidence (${confidence.toFixed(2)}) for: ${speakerLabel}`)
    }
    
    return `[${speakerLabel}]: ${message}`
  }

  const cleanup = () => {
    if (deepgramConnection) {
      deepgramConnection.requestClose()
    }
    deepgramConnection = null
    deepgramClient = null
    if (speakerSocket) {
      speakerSocket.close()
      speakerSocket = null
    }
    
    // Clear translation queue
    translationQueue.clear()
    requestIdCounter = 0
  }

  return {
    connectToDeepgram,
    connectToSpeakerSocket,
    cleanup
  }
}
